import React, { useEffect, useState } from 'react';
import { TrendingUp, Coins, Calendar, Clock, BarChart3, Loader2 } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Customer-facing EVC stats card.
 * Shows lifetime totals + 30/90/365-day breakdowns + 12-month bar chart.
 * Adapts to mobile (stacks tiles 2-up, shrinks chart height).
 */
export default function EVCStats({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/evc/customer/me/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok && !cancelled) setData(await r.json());
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-6" data-testid="evc-stats">
        <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading stats…</div>
      </section>
    );
  }
  if (!data || (data.totals?.purchases || 0) === 0) {
    return null; // Don't clutter the dashboard for new users with no purchases.
  }

  const t = data.totals;
  const m = data.by_month || [];

  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-6" data-testid="evc-stats">
      <h2 className="font-semibold text-slate-800 flex items-center gap-2 mb-4">
        <BarChart3 className="w-4 h-4" /> Your EVC stats
      </h2>

      {/* Top tiles — lifetime + 30/90/365d. Mobile: 2 cols, Desktop: 4 cols */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3" data-testid="evc-stats-tiles">
        <Tile
          label="All time" testid="evc-stat-all"
          big={`${t.credits}`} sub={`credits over ${t.purchases} purchase${t.purchases === 1 ? '' : 's'}`}
          extra={`€${t.spent.toFixed(2)} spent`}
          icon={<TrendingUp className="w-4 h-4" />} color="amber"
        />
        <Tile
          label="Last 30 days" testid="evc-stat-30d"
          big={`${data.last_30d.credits}`} sub={`credits · ${data.last_30d.purchases} buys`}
          extra={`€${data.last_30d.spent.toFixed(2)}`}
          icon={<Clock className="w-4 h-4" />} color="emerald"
        />
        <Tile
          label="Last 90 days" testid="evc-stat-90d"
          big={`${data.last_90d.credits}`} sub={`credits · ${data.last_90d.purchases} buys`}
          extra={`€${data.last_90d.spent.toFixed(2)}`}
          icon={<Calendar className="w-4 h-4" />} color="cyan"
        />
        <Tile
          label="Last 12 months" testid="evc-stat-365d"
          big={`${data.last_365d.credits}`} sub={`credits · ${data.last_365d.purchases} buys`}
          extra={`€${data.last_365d.spent.toFixed(2)}`}
          icon={<Coins className="w-4 h-4" />} color="violet"
        />
      </div>

      {/* 12-month chart — credits per month */}
      <div className="mt-5">
        <div className="flex items-baseline justify-between mb-2">
          <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Credits purchased per month</h3>
          {t.first_purchase && (
            <span className="text-[10px] text-slate-400">since {t.first_purchase.slice(0, 10)}</span>
          )}
        </div>
        <div className="h-40 sm:h-48 w-full" data-testid="evc-stats-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={m} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748b' }} interval={0} tickFormatter={(v) => v?.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: '#64748b' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0' }}
                formatter={(value, name) => name === 'spent' ? [`€${Number(value).toFixed(2)}`, 'Spent'] : [value, name === 'credits' ? 'Credits' : name]}
                labelFormatter={(l) => `Month: ${l}`}
              />
              <Bar dataKey="credits" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  );
}

function Tile({ label, big, sub, extra, icon, color, testid }) {
  const palettes = {
    amber:   'from-amber-50 to-orange-50 border-amber-200 text-amber-700',
    emerald: 'from-emerald-50 to-teal-50 border-emerald-200 text-emerald-700',
    cyan:    'from-cyan-50 to-blue-50 border-cyan-200 text-cyan-700',
    violet:  'from-violet-50 to-fuchsia-50 border-violet-200 text-violet-700',
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-br p-3 sm:p-4 ${palettes[color]}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] sm:text-xs uppercase tracking-wider opacity-80">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-xl sm:text-2xl font-extrabold leading-none">{big}</span>
        <span className="text-[10px] sm:text-xs opacity-70">credits</span>
      </div>
      <div className="text-[10px] sm:text-xs text-slate-500 mt-0.5 truncate">{sub}</div>
      <div className="text-xs sm:text-sm font-semibold mt-1">{extra}</div>
    </div>
  );
}
